<template>
  <PaginaAdministrar
    module-name="Configurações do Sistema"
    :loading="loading"
    :erro="erro"
    :mostrar-botao-adicionar="false"
    @atualizar="buscarDados"
  >
    <AdminTable
      :columns="colunas"
      :data="configs"
      :loading="loading"
    >
      <template #valor="{ row }">
        <span v-if="!editando || editandoId !== row.idConfiguracaoSistema">
          <strong>{{ row.valor }}</strong>
        </span>
        <div v-else class="d-flex align-items-center gap-2">
          <input
            type="number"
            class="form-control form-control-sm"
            style="width: 120px;"
            v-model.number="editandoValor"
            :min="row.valorMinimo"
            :max="row.valorMaximo"
          />
          <button class="btn btn-sm btn-success" @click="salvarEdicao(row)" :disabled="salvando">
            <i v-if="salvando" class="spinner-border spinner-border-sm"></i>
            <i v-else class="bi bi-check"></i>
          </button>
          <button class="btn btn-sm btn-outline-secondary" @click="cancelarEdicao">
            <i class="bi bi-x"></i>
          </button>
        </div>
      </template>
      <template #modulo="{ row }">
        <span class="badge badge-ghost-secondary">{{ row.modulo || '-' }}</span>
      </template>
      <template #acoes="{ row }">
        <button
          class="btn btn-sm btn-outline-primary"
          @click="iniciarEdicao(row)"
          :disabled="editando && editandoId !== row.idConfiguracaoSistema"
          title="Editar"
        >
          <i class="bi bi-pencil"></i>
        </button>
      </template>
    </AdminTable>
  </PaginaAdministrar>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'

const emit = defineEmits(['show-notification'])

const loading = ref(false)
const erro = ref(null)
const configs = ref([])
const editando = ref(false)
const editandoId = ref(null)
const editandoValor = ref('')
const salvando = ref(false)

const colunas = computed(() => [
  { field: 'chave', label: 'Chave', sortable: true },
  { field: 'valor', label: 'Valor', width: 200, slotName: 'valor' },
  { field: 'descricao', label: 'Descrição', sortable: true },
  { field: 'modulo', label: 'Módulo', width: 120, slotName: 'modulo' },
  { field: 'acoes', label: '', width: 70, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
])

const buscarDados = async () => {
  loading.value = true
  erro.value = null
  try {
    const response = await api.get('/ConfiguracoesSistema')
    configs.value = response.data
  } catch (error) {
    if (error.response?.status === 403) {
      configs.value = []
    } else {
      erro.value = 'Falha ao carregar configurações.'
      emit('show-notification', { message: 'Erro ao carregar configurações.', type: 'error' })
    }
  } finally {
    loading.value = false
  }
}

const iniciarEdicao = (config) => {
  editando.value = true
  editandoId.value = config.idConfiguracaoSistema
  editandoValor.value = config.valor
}

const cancelarEdicao = () => {
  editando.value = false
  editandoId.value = null
  editandoValor.value = ''
}

const salvarEdicao = async (config) => {
  const valorNum = Number(editandoValor.value)
  if (config.valorMinimo && valorNum < Number(config.valorMinimo)) {
    emit('show-notification', { message: `O valor mínimo permitido é ${config.valorMinimo}.`, type: 'warning' })
    return
  }
  if (config.valorMaximo && valorNum > Number(config.valorMaximo)) {
    emit('show-notification', { message: `O valor máximo permitido é ${config.valorMaximo}.`, type: 'warning' })
    return
  }
  salvando.value = true
  try {
    await api.put(`/ConfiguracoesSistema/${config.idConfiguracaoSistema}`, {
      chave: config.chave,
      valor: editandoValor.value,
      descricao: config.descricao,
      modulo: config.modulo
    })
    cancelarEdicao()
    emit('show-notification', { message: 'Configuração salva com sucesso!', type: 'success' })
    await buscarDados()
  } catch (error) {
    if (error.response?.status === 403) return
    emit('show-notification', { message: 'Erro ao salvar configuração.', type: 'danger' })
  } finally {
    salvando.value = false
  }
}

defineExpose({ buscarDados })

onMounted(() => {
  buscarDados()
})
</script>
